// Thin wrappers around the Worker API + R2 data fetches. All shared
// between the viewer and any future pages; bundled into the viewer chunk.

const CFG = window.PICKER_CONFIG!;
const KEY_STORAGE = "orion_picker_key";

export function getKey(): string {
  const fromUrl = new URLSearchParams(location.search).get("key");
  if (fromUrl) {
    localStorage.setItem(KEY_STORAGE, fromUrl);
    const u = new URL(location.href);
    u.searchParams.delete("key");
    history.replaceState({}, "", u.toString());
    return fromUrl;
  }
  // Fallback chain: localStorage → committed config.js → empty
  return localStorage.getItem(KEY_STORAGE) || (CFG as any).PICKER_KEY || "";
}

export function dataUrl(path: string): string {
  return `${CFG.DATA_BASE.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
}

// Dataset selector via ?dataset=… . Default (Orion) reads manifest.json +
// samples/<sid>.json; "tma" reads the parallel manifest_tma.json +
// samples_tma/<sid>.json so the Eirini COL4/AT8 TMA lives beside Orion without
// polluting the reviewers' roster. Empty string = default dataset.
export function dataset(): string {
  return new URLSearchParams(location.search).get("dataset") || "";
}

export function manifestUrl(): string {
  return dataset() === "tma" ? "manifest_tma.json" : "manifest.json";
}

// L0 pixel size (µm) derived from the sid, NOT the current dataset — the "My
// picks" list mixes Orion and TMA picks. Eirini TMA fields end in _R/_M and are
// 0.25487 µm/px; every Orion sample is 0.325 µm/px. Mirrors export_worker._is_tma.
export const ORION_PX_UM = 0.325;
export const TMA_PX_UM = 0.25487304459235771;
export function pxUmForSid(sid: string): number {
  return /_[RM]$/.test(sid || "") ? TMA_PX_UM : ORION_PX_UM;
}

export async function api(path: string, opts: RequestInit = {}) {
  const headers: Record<string, string> = {
    "X-Picker-Key": getKey(),
    ...(opts.headers as Record<string, string>),
  };
  if (opts.body) headers["Content-Type"] = "application/json";
  const r = await fetch(CFG.API_BASE + path, {...opts, headers});
  if (r.status === 401) {
    localStorage.removeItem(KEY_STORAGE);
    throw new Error("unauthorized");
  }
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
  if (r.status === 204) return null;
  return r.json();
}

export interface SampleData {
  sid: string;
  donor_id: string;
  condition: string;
  zarr_url: string;
  channels: Array<{
    label: string;
    color: string;       // "#RRGGBB"
    active: boolean;
    window: { start: number; end: number; min?: number; max?: number };
  }>;
  wm_polygon: Array<Array<[number, number]>>;
  px_size_um: number;
}

export async function loadSample(sid: string): Promise<SampleData> {
  const dir = dataset() === "tma" ? "samples_tma" : "samples";
  return (await fetch(dataUrl(`${dir}/${sid}.json`), {cache: "no-store"})).json();
}

export interface MarkerWindow {
  lo: number;
  hi: number;
  color: string | null;              // "#RRGGBB" | null
  updated_by?: string | null;
  updated_at?: string | null;
}

// Lab-canonical per-marker windows: ONE lo/hi/color per marker, shared by
// everyone and both disease groups. Applied client-side so a change re-colors
// instantly with no re-crop. Same source the Exports page edits.
export async function loadWindows(): Promise<Record<string, MarkerWindow>> {
  try { return (await api("/windows")).windows || {}; }
  catch { return {}; }
}

export async function saveWindow(
  marker: string,
  w: {lo: number; hi: number; color?: string | null},
  updatedBy?: string,
) {
  return api("/windows", {method: "POST", body: JSON.stringify({
    marker,
    lo: Math.round(w.lo),
    hi: Math.round(w.hi),
    color: w.color || undefined,
    updated_by: updatedBy || undefined,
  })});
}

// Queue a full-res export ("Save to Drive") for a saved pick. The Worker marks
// the pick export_status='requested'; the Minerva poller does the actual crop
// + upload and flips it to done. Returns the updated pick fields.
export async function requestExport(uuid: string) {
  return api("/export", {method: "POST", body: JSON.stringify({uuid})});
}

export interface Pick {
  uuid: string;
  sid: string;
  marker: string;
  x_l0: number; y_l0: number; w_l0: number; h_l0: number;
  wm_overlap?: number;
  channels_on?: string[] | null;
  colormap?: Record<string, string> | null;         // label -> "#RRGGBB"
  contrast?: Record<string, [number, number]> | null; // label -> [lo, hi]
  viewport?: {target?: [number, number]; zoom?: number} | null;
  notes?: string | null;
  created_at: string;
  // Export ("Save to Drive") lifecycle — populated by the worker.
  export_status?: string | null;   // requested | running | done | failed
  export_url?: string | null;      // R2 folder URL when done
  export_at?: string | null;
}

// The Worker returns picks two ways: freshly-POSTed picks carry parsed
// objects (colormap/contrast/viewport), while GET /picks returns raw D1 rows
// with *_json string columns. Normalize both to the same shape so the viewer
// can restore state + show export status uniformly.
function parseMaybe(v: any): any {
  if (v == null) return null;
  if (typeof v !== "string") return v;
  try { return JSON.parse(v); } catch { return null; }
}

export function parsePicks(rows: any[]): Pick[] {
  return rows.map(r => ({
    ...r,
    channels_on: parseMaybe(r.channels_on),
    colormap:    parseMaybe(r.colormap ?? r.colormap_json),
    contrast:    parseMaybe(r.contrast ?? r.contrast_json),
    viewport:    parseMaybe(r.viewport ?? r.viewport_state),
  }));
}
